// @ts-nocheck
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DateTime } from 'luxon'
import { Phone, Calendar, Filter, ChevronLeft, ChevronRight, Search, RotateCcw, RefreshCw, User, Download } from 'lucide-react'
import { useCallHistory } from '../hooks/useCallHistory'
import { useApi } from '../hooks/useApi'
import { useAuthStore } from '../stores/authStore'
import CallHistoryTable from '../components/CallHistoryTable'
import { OutboundCallDialog } from '../components/OutboundCallDialog'

// ポーリング間隔（ミリ秒）
const POLLING_INTERVAL = 30000 // 30秒

export default function CallHistoryPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const api = useApi()
  const { user } = useAuthStore()

  // 電話番号情報（着信先名表示用）
  const [phoneNumbers, setPhoneNumbers] = useState({})

  // 折り返し発信ダイアログ
  const [isCallbackDialogOpen, setIsCallbackDialogOpen] = useState(false)
  const [selectedCallHistory, setSelectedCallHistory] = useState(null)

  // URLパラメータからフィルタ状態を取得
  const getInitialDateRange = () => {
    const startDate = searchParams.get('startDate')
    const endDate = searchParams.get('endDate')
    return {
      startDate: startDate || DateTime.now().minus({ days: 30 }).toISODate(),
      endDate: endDate || DateTime.now().toISODate()
    }
  }

  // フィルタ状態（URLパラメータから初期化）
  const [dateRange, setDateRange] = useState(getInitialDateRange)
  const [callTypeFilter, setCallTypeFilter] = useState(searchParams.get('callType') || 'all')
  const [myCallsOnly, setMyCallsOnly] = useState(searchParams.get('myCallsOnly') === 'true')
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)

  // ポーリング用
  const pollingRef = useRef(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const isAudioPlayingRef = useRef(false)

  // useCallHistory フック
  const {
    callHistory,
    loading,
    error,
    pagination,
    fetchCallHistory
  } = useCallHistory({
    limit: 20,
    autoFetch: false
  })

  // 電話番号情報を取得（着信先名表示用）
  useEffect(() => {
    if (!user?.organizationId) return

    const fetchPhoneNumbers = async () => {
      try {
        const data = await api.get('/api/phone-numbers')
        // 電話番号をキーとするマップを作成
        const phoneNumberMap = {}
        data.items?.forEach(item => {
          phoneNumberMap[item.phoneNumber] = item
        })
        setPhoneNumbers(phoneNumberMap)
      } catch (err) {
        console.error('電話番号情報取得エラー:', err)
      }
    }

    fetchPhoneNumbers()
  }, [api, user?.organizationId])

  // 手動リフレッシュ
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      const params = {
        page: currentPage,
        limit: 20,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        sortBy: 'startTime',
        sortOrder: 'desc'
      }

      if (callTypeFilter !== 'all') {
        params.callType = callTypeFilter
      }
      if (myCallsOnly) {
        params.myCallsOnly = true
      }

      await fetchCallHistory(params)
      setLastUpdated(new Date())
    } finally {
      setIsRefreshing(false)
    }
  }, [currentPage, dateRange, callTypeFilter, myCallsOnly, fetchCallHistory])

  // URLパラメータを更新
  const updateSearchParams = (newParams) => {
    const params = new URLSearchParams()

    const finalParams = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      callType: callTypeFilter,
      myCallsOnly: myCallsOnly,
      page: currentPage.toString(),
      ...newParams
    }

    // デフォルト値と同じ場合はURLに含めない
    if (finalParams.startDate) params.set('startDate', finalParams.startDate)
    if (finalParams.endDate) params.set('endDate', finalParams.endDate)
    if (finalParams.callType && finalParams.callType !== 'all') params.set('callType', finalParams.callType)
    if (finalParams.myCallsOnly) params.set('myCallsOnly', 'true')
    if (finalParams.page && finalParams.page !== '1') params.set('page', finalParams.page)

    setSearchParams(params, { replace: true })
  }

  // 現在のURLパラメータ文字列を生成（詳細ページに渡すため）
  const currentSearchParamsString = useMemo(() => {
    const params = new URLSearchParams()
    params.set('startDate', dateRange.startDate)
    params.set('endDate', dateRange.endDate)
    if (callTypeFilter !== 'all') params.set('callType', callTypeFilter)
    if (myCallsOnly) params.set('myCallsOnly', 'true')
    if (currentPage !== 1) params.set('page', currentPage.toString())
    return params.toString()
  }, [dateRange, callTypeFilter, myCallsOnly, currentPage])

  // 通話履歴取得
  useEffect(() => {
    const params = {
      page: currentPage,
      limit: 20,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: 'startTime',
      sortOrder: 'desc'
    }

    if (callTypeFilter !== 'all') {
      params.callType = callTypeFilter
    }
    if (myCallsOnly) {
      params.myCallsOnly = true
    }

    fetchCallHistory(params).then(() => {
      setLastUpdated(new Date())
    }).catch(() => {})
  }, [currentPage, dateRange, callTypeFilter, myCallsOnly])

  // ポーリング（30秒ごとに更新、録音再生中はスキップ）
  useEffect(() => {
    pollingRef.current = setInterval(() => {
      if (isAudioPlayingRef.current) return
      handleRefresh()
    }, POLLING_INTERVAL)

    // クリーンアップ
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [handleRefresh])

  // フィルタ変更時にURLを更新
  useEffect(() => {
    updateSearchParams({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      callType: callTypeFilter,
      myCallsOnly: myCallsOnly,
      page: currentPage.toString()
    })
  }, [dateRange, callTypeFilter, myCallsOnly, currentPage])

  // ページ変更
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
  }

  // 詳細ページへ遷移
  const handleViewDetail = (call) => {
    navigate(`/call-history/${call.callHistoryId}`, {
      state: { searchParams: currentSearchParamsString }
    })
  }

  // 日付範囲変更
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange)
    setCurrentPage(1)
  }

  // フィルタ変更
  const handleCallTypeChange = (value) => {
    setCallTypeFilter(value)
    setCurrentPage(1)
  }

  const handleMyCallsOnlyToggle = () => {
    setMyCallsOnly(prev => !prev)
    setCurrentPage(1)
  }

  // フィルタがアクティブかどうか
  const hasActiveFilters = callTypeFilter !== 'all' || myCallsOnly

  // フィルタをリセット
  const handleResetFilters = () => {
    setCallTypeFilter('all')
    setMyCallsOnly(false)
    handleDateRangeChange({
      startDate: DateTime.now().minus({ days: 30 }).toISODate(),
      endDate: DateTime.now().toISODate()
    })
  }

  // 折り返し発信
  const handleCallback = (call) => {
    setSelectedCallHistory(call)
    setIsCallbackDialogOpen(true)
  }

  // ダイアログを閉じる
  const handleCloseCallbackDialog = () => {
    setIsCallbackDialogOpen(false)
    setSelectedCallHistory(null)
  }

  // CSVエクスポート
  const handleExport = async () => {
    try {
      const params = new URLSearchParams()
      
      // 日付範囲
      if (dateRange.startDate) params.append('startDate', dateRange.startDate)
      if (dateRange.endDate) params.append('endDate', dateRange.endDate)
      
      // フィルタ
      if (callTypeFilter !== 'all') params.append('callType', callTypeFilter)
      if (myCallsOnly) params.append('myCallsOnly', 'true')

      // APIエンドポイントを呼び出してCSVをダウンロード
      const response = await api.getRaw(`/api/call-history/export?${params.toString()}`)
      
      // Blobを作成してダウンロード
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      
      // ファイル名を生成
      const timestamp = DateTime.now().toFormat('yyyy-MM-dd')
      a.download = `call_history_${timestamp}.csv`
      
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('エクスポートエラー:', error)
      alert('エクスポートに失敗しました')
    }
  }

  return (
    <div className="p-6">
      {/* ヘッダー */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">通話履歴</h1>
              <p className="text-sm text-gray-500 mt-0.5">すべての通話記録を検索・閲覧</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* エクスポートボタン */}
            <button
              onClick={handleExport}
              disabled={loading || callHistory.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              title="CSVエクスポート"
            >
              <Download className="w-4 h-4" />
              エクスポート
            </button>
            
            {/* リフレッシュボタン */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="更新"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {lastUpdated && (
                <span className="text-xs text-gray-400">
                  {lastUpdated.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </button>
            {pagination.total > 0 && !loading && (
              <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">
                全 <span className="font-semibold text-gray-700">{pagination.total}</span> 件
              </div>
            )}
          </div>
        </div>
      </div>

      {/* フィルタ */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200">
        {/* フィルタヘッダー */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Filter className="w-4 h-4 text-gray-500" />
            検索フィルタ
            {hasActiveFilters && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                フィルタ適用中
              </span>
            )}
          </div>
          {hasActiveFilters && (
            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              <RotateCcw className="w-3 h-3" />
              リセット
            </button>
          )}
        </div>

        <div className="p-5">
          {/* 期間プリセット（上部に配置） */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-500">期間プリセット</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { label: '今日', start: DateTime.now().toISODate(), end: DateTime.now().toISODate() },
                { label: '過去7日間', start: DateTime.now().minus({ days: 7 }).toISODate(), end: DateTime.now().toISODate() },
                { label: '過去30日間', start: DateTime.now().minus({ days: 30 }).toISODate(), end: DateTime.now().toISODate() },
                { label: '今月', start: DateTime.now().startOf('month').toISODate(), end: DateTime.now().toISODate() },
                { label: '先月', start: DateTime.now().minus({ months: 1 }).startOf('month').toISODate(), end: DateTime.now().minus({ months: 1 }).endOf('month').toISODate() }
              ].map((preset) => {
                const isActive = dateRange.startDate === preset.start && dateRange.endDate === preset.end
                return (
                  <button
                    key={preset.label}
                    onClick={() => handleDateRangeChange({ startDate: preset.start, endDate: preset.end })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {preset.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* フィルタグリッド */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* 開始日 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                開始日
              </label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateRangeChange({ ...dateRange, startDate: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* 終了日 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                終了日
              </label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateRangeChange({ ...dateRange, endDate: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* 通話種別 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                通話種別
              </label>
              <select
                value={callTypeFilter}
                onChange={(e) => handleCallTypeChange(e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                  callTypeFilter !== 'all' ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
                }`}
              >
                <option value="all">すべて</option>
                <option value="inquiry">問い合わせ</option>
                <option value="complaint">クレーム</option>
                <option value="order">注文</option>
                <option value="other">その他</option>
              </select>
            </div>

            {/* 自分の通話のみ */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                担当者
              </label>
              <button
                onClick={handleMyCallsOnlyToggle}
                className={`w-full px-3 py-2.5 border rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  myCallsOnly
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <User className="w-4 h-4" />
                {myCallsOnly ? '自分の通話のみ表示中' : '自分の通話のみ'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg shadow-sm">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* ローディング表示 */}
      {loading ? (
        <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <span className="text-sm text-gray-500">読み込み中...</span>
          </div>
        </div>
      ) : (
        <>
          {/* 通話履歴テーブル */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <CallHistoryTable
              callHistory={callHistory}
              onEditMemo={handleViewDetail}
              onCallback={handleCallback}
              displayMode="full"
              emptyMessage="通話履歴がありません"
              phoneNumbers={phoneNumbers}
              onPlaybackChange={(playing) => { isAudioPlayingRef.current = playing }}
            />
          </div>

          {/* ページネーション */}
          {pagination.totalPages > 1 && (
            <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  <ChevronLeft className="w-4 h-4" />
                  前へ
                </button>

                <div className="flex items-center gap-2">
                  {/* ページ番号ボタン */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                  <span className="text-sm text-gray-500 ml-2">
                    / {pagination.totalPages} ページ
                  </span>
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= pagination.totalPages}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  次へ
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* 折り返し発信ダイアログ */}
      <OutboundCallDialog
        isOpen={isCallbackDialogOpen}
        onClose={handleCloseCallbackDialog}
        callbackCallHistory={selectedCallHistory}
      />
    </div>
  )
}
